import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {Box, Card, Grid, Section, SectionIntroStacked, Statistic} from '@primer/react-brand/esm'
import type {ReactElement} from 'react'
import {getPrimerIcon} from '@github-ui/swp-core/lib/utils/icons'
import {getImageSources, MAX_CONTENT_WIDTH} from '@github-ui/swp-core/lib/utils/images'
import {FlexSuiteIntroLogos} from '../../../components/FlexSuiteIntroLogos'
import {FlexSuiteIntroPillars} from '../../../components/FlexSuiteIntroPillars'
import {hasIntroSemanticHeading, renderWithoutParagraphs} from '../../../utils'
import type {IntroSectionProps} from './types'
import baseStyles from '../../../styles/base.module.css'

type IntroContextContentProps = Pick<
  IntroSectionProps,
  | 'introContext'
  | 'introContextHeading'
  | 'introContextLink'
  | 'introContextBody'
  | 'introItems'
  | 'introIconCards'
  | 'introPillars'
  | 'introImageCards'
  | 'introStats'
> & {promoteFirstHeading: boolean}

export function IntroSection({
  hasPriorSemanticH2 = false,
  introLogoHeading,
  introLogos,
  introLogoLink,
  introContext,
  introContextHeading,
  introContextLink,
  introContextBody,
  introItems,
  introIconCards,
  introPillars,
  introImageCards,
  introStats,
  logobarOptions,
  logoSuiteOptions,
  hideLogoHeading = true,
}: IntroSectionProps) {
  const promoteFirstHeading =
    !hasPriorSemanticH2 &&
    !hasIntroSemanticHeading({
      hasLogoHeading: Boolean(introLogos?.length),
      hasIntroContextHeading: Boolean(introContextHeading),
    })
  const introContextContent = renderIntroContextContent({
    introContext,
    introContextHeading,
    introContextLink,
    introContextBody,
    introItems,
    introIconCards,
    introPillars,
    introImageCards,
    introStats,
    promoteFirstHeading,
  })

  const hasIntroContent = Boolean(introLogos?.length) || introContextContent !== null

  if (!hasIntroContent) return null

  const takeoverButton =
    logobarOptions?.takeoverButton ??
    (introLogoLink?.fields.href ? {label: introLogoLink.fields.text, href: introLogoLink.fields.href} : undefined)

  return (
    <Section fullWidth paddingBlockEnd="none" paddingBlockStart="none">
      <FlexSuiteIntroLogos
        heading={introLogoHeading}
        logos={introLogos}
        logobarOptions={{...logobarOptions, takeoverButton}}
        logoSuiteOptions={logoSuiteOptions}
        hideHeading={hideLogoHeading}
      />
      {introContextContent}
    </Section>
  )
}

function renderIntroContextContent({
  introContext,
  introContextHeading,
  introContextLink,
  introContextBody,
  introItems,
  introIconCards,
  introPillars,
  introImageCards,
  introStats,
  promoteFirstHeading,
}: IntroContextContentProps): ReactElement | null {
  if (!introContext) return null

  const firstIntroItemHeadingIndex = introItems?.findIndex(item => Boolean(item.fields.heading)) ?? -1

  switch (introContext) {
    case 'Simple Intro':
      if (!introContextHeading && !introContextBody) return null
      return (
        <Box marginBlockStart={{narrow: 48, regular: 80}} marginBlockEnd={{narrow: 48, regular: 80}}>
          <Grid>
            <Grid.Column>
              <SectionIntroStacked>
                {introContextHeading ? (
                  <SectionIntroStacked.Heading>
                    {renderWithoutParagraphs(introContextHeading)}
                  </SectionIntroStacked.Heading>
                ) : null}
                {introContextBody ? (
                  <SectionIntroStacked.Description>
                    {renderWithoutParagraphs(introContextBody)}
                  </SectionIntroStacked.Description>
                ) : null}
                {introContextLink?.fields.href && (
                  <SectionIntroStacked.Link href={introContextLink.fields.href}>
                    {introContextLink.fields.text}
                  </SectionIntroStacked.Link>
                )}
                {introItems && introItems.length > 0 ? (
                  <SectionIntroStacked.Items>
                    {introItems.map((item, index) => {
                      const isIconItem = item.sys.contentType.sys.id === 'iconItem'
                      const IconComponent = isIconItem && 'icon' in item.fields ? getPrimerIcon(item.fields.icon) : null
                      return (
                        <SectionIntroStacked.Item key={item.sys.id}>
                          {IconComponent ? <SectionIntroStacked.ItemIcon icon={<IconComponent />} /> : null}
                          {item.fields.heading ? (
                            <SectionIntroStacked.ItemHeading
                              as={index === firstIntroItemHeadingIndex && promoteFirstHeading ? 'h2' : 'h3'}
                              size="subhead-large"
                            >
                              {renderWithoutParagraphs(item.fields.heading)}
                            </SectionIntroStacked.ItemHeading>
                          ) : null}
                          {item.fields.description ? (
                            <SectionIntroStacked.ItemDescription>
                              {renderWithoutParagraphs(item.fields.description)}
                            </SectionIntroStacked.ItemDescription>
                          ) : null}
                        </SectionIntroStacked.Item>
                      )
                    })}
                  </SectionIntroStacked.Items>
                ) : null}
              </SectionIntroStacked>
            </Grid.Column>
          </Grid>
        </Box>
      )
    case 'Pillars': {
      if (!introPillars || introPillars.length === 0) return null
      return <FlexSuiteIntroPillars introPillars={introPillars} promoteFirstHeading={promoteFirstHeading} />
    }
    case 'Icon Cards':
      if (!introIconCards || introIconCards.length === 0) return null
      return (
        <Box className={baseStyles.cardGridFrame}>
          <Grid className={baseStyles.gridContent}>
            {introIconCards.map((item, index) => {
              const IconComponent = getPrimerIcon(item.fields.icon)
              return (
                /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                <Grid.Column key={item.sys.id} span={{medium: 4}} className="gridColumn">
                  <Box
                    className={baseStyles.cardGridItem}
                    padding={{narrow: 24, regular: 40}}
                    paddingBlockStart={{regular: 80}}
                    paddingBlockEnd={{regular: 80}}
                    borderWidth="thin"
                    borderColor="muted"
                  >
                    <Card href={item.fields.href} variant="minimal" fullWidth ctaText={item.fields.label}>
                      {IconComponent ? <Card.Icon icon={<IconComponent />} color="default" hasBackground /> : null}
                      {item.fields.eyebrow ? <Card.Label>{item.fields.eyebrow}</Card.Label> : null}
                      <Card.Heading as={index === 0 && promoteFirstHeading ? 'h2' : 'h3'} size="6">
                        {renderWithoutParagraphs(item.fields.heading)}
                        {item.fields.alt ? <span className="sr-only"> {item.fields.alt}</span> : null}
                      </Card.Heading>
                      <Card.Description>{renderWithoutParagraphs(item.fields.description)}</Card.Description>
                    </Card>
                  </Box>
                </Grid.Column>
              )
            })}
          </Grid>
        </Box>
      )
    case 'Image Cards':
      if (!introImageCards || introImageCards.length === 0) return null
      return (
        <Box marginBlockStart={48}>
          <Grid>
            {introImageCards.map((card, index) => (
              <Grid.Column key={card.sys.id} span={{xsmall: 12, medium: 6, large: 4}}>
                <Card href={card.fields.href} variant="minimal" fullWidth ctaText={card.fields.label}>
                  <Card.Image
                    src={`${card.fields.image.fields.file.url}?fm=webp`}
                    alt={card.fields.image.fields.description || documentToPlainTextString(card.fields.heading) || ''}
                    sources={getImageSources(card.fields.image.fields.file.url, {
                      maxWidth: MAX_CONTENT_WIDTH / 3,
                    })}
                  />
                  <Card.Heading as={index === 0 && promoteFirstHeading ? 'h2' : 'h3'} size="6">
                    {renderWithoutParagraphs(card.fields.heading)}
                    {card.fields.alt ? <span className="sr-only"> {card.fields.alt}</span> : null}
                  </Card.Heading>
                  <Card.Description>{renderWithoutParagraphs(card.fields.description)}</Card.Description>
                </Card>
              </Grid.Column>
            ))}
          </Grid>
        </Box>
      )
    case 'Stats':
      if (!introStats || introStats.length === 0) return null
      return (
        <Box marginBlockStart={{narrow: 48, regular: 80}} marginBlockEnd={{narrow: 48, regular: 80}}>
          <Grid>
            {introStats.map(stat => (
              <Grid.Column key={stat.sys.id} span={{medium: 4}}>
                <Statistic>
                  <Statistic.Heading>{renderWithoutParagraphs(stat.fields.heading)}</Statistic.Heading>
                  <Statistic.Description>{renderWithoutParagraphs(stat.fields.description)}</Statistic.Description>
                </Statistic>
              </Grid.Column>
            ))}
          </Grid>
        </Box>
      )
    default:
      return null
  }
}
